/**
 * Roteador (§5.3.1; FE §3.9, §7.4): ÚNICO escritor de `route` e único lugar (com state/actions.ts) que toca
 * `location.hash` e `history.*` (AC-5.1-02). Nunca `history.pushState` (AC-5.3-07).
 * - `navigate(partial, { replace, then })`: mescla, normaliza com as regras da leitura, grava o hash (push por
 *   `location.hash`, ou `replaceState` + escrita do sinal na mesma chamada) e roda `afterRouteChange`.
 * - `initRoute(hash)`: 1ª escrita de `route` (montagem, lê `tv=1`), canonização com `replaceState`, e instala o ouvinte
 *   único de `hashchange` e a releitura quando `ctx` muda (estáticos, §5.2.9). Devolve o desligamento.
 * - `routerPort`: o que state/actions.ts recebe por `bindRouter()` (o roteador não importa actions.ts, §2.2.2).
 * Em TV o roteador não reescreve o hash (a cena dirige a vista e `tv=1` fica, §5.2.7).
 */
import { effect, untracked } from '@preact/signals';
import type { LayoutMode, RouteState } from '../model/types';
import { ctx, isSimulated, layout, type RouteContext, route, ui } from '../state/store';
import { afterRouteChange, applyTitle, closeIncoherentProfile, setPendingThen, setRoutePending } from './effects';
import { formatRoute, withTvParam } from './formatRoute';
import { normalizeRoute, parseRoute } from './parseRoute';
import { sameRoute } from './selectors';
import type { NavigateOptions, ParseResult, RouteChangeCause, RouterPortLike } from './types';

// O app controla a rolagem do `flow` (§4.6); o navegador não restaura posição ao voltar (§5.3.1).
try {
  if (typeof history !== 'undefined' && 'scrollRestoration' in history) history.scrollRestoration = 'manual';
} catch {
  // navegador sem suporte: ignorado
}

const PHONE_MODES: ReadonlySet<LayoutMode> = new Set(['flow', 'tablet', 'land']);

/** Push em voo: o `hashchange` que ele vai disparar usa esta rota (com a memória do modo do mapa) em vez de reler. */
let expected: { hash: string; result: ParseResult } | null = null;
let lastCtx: RouteContext | null = null;
let stopFn: (() => void) | null = null;

function replaceHash(hash: string): void {
  history.replaceState(history.state, '', hash);
}

/**
 * 26/10 (abertura pós-apuração): enquanto `keepBare()` valer, um endereço sem rota (`''`, `#`, `#/`) não ganha o hash
 * canônico (`#/presidente`) na montagem nem na releitura dos estáticos; a rota em vigor é a mesma. Padrão: nunca.
 */
let keepBare: () => boolean = () => false;
const BARE_HASH = /^#?\/?$/;

export function holdBareHashWhile(fn: () => boolean): void {
  keepBare = fn;
}

function holdBare(hash: string): boolean {
  return BARE_HASH.test(hash) && keepBare();
}

/** Grava no endereço o hash canônico da rota em vigor (abertura pós-apuração → apuração). Em TV, nada. */
export function canonizeHash(): void {
  if (typeof location === 'undefined' || ui.value.tv.on) return;
  const r = normalizeRoute(currentRoute(), ctx.value);
  if (r.canonical !== location.hash) replaceHash(r.canonical);
}

/** Escreve `route` (só se mudou) e roda os efeitos. */
function commit(prev: RouteState, r: ParseResult, cause: RouteChangeCause): void {
  setRoutePending(r.pending);
  const next = sameRoute(prev, r.route) ? prev : r.route;
  if (next !== prev) route.value = next;
  afterRouteChange(prev, next, cause);
}

/**
 * Rota em vigor para a próxima navegação: a do push em voo (o `hashchange` dele ainda não rodou), senão `route`. Sem
 * isso, duas navegações seguidas antes do `hashchange` (teclas "2" e "1" com 12 ms entre elas) partiam do sinal
 * atrasado e a segunda virava no-op (CI do `main` da Fase 3, 09/10).
 */
export function currentRoute(): RouteState {
  return expected?.result.route ?? route.value;
}

/**
 * §5.3.1. Em TV não faz nada (§5.5.9). Hash igual ao atual: nada é escrito, mas o foco pedido e o `then` rodam. Mescla
 * sobre `currentRoute()`, não sobre `route`.
 */
export function navigate(partial: Partial<RouteState>, opts: NavigateOptions = {}): void {
  if (ui.value.tv.on) return;
  const prev = route.value;
  const r = normalizeRoute({ ...currentRoute(), ...partial }, ctx.value);
  setPendingThen(opts.then ?? null);
  if (r.canonical === location.hash) {
    if (expected && expected.hash === r.canonical) {
      expected = { hash: r.canonical, result: r }; // mesmo destino do push em voo: o hashchange dele aplica
      return;
    }
    commit(prev, r, 'same');
    return;
  }
  if (opts.replace) {
    replaceHash(r.canonical);
    commit(prev, r, 'navigate');
    return;
  }
  expected = { hash: r.canonical, result: r };
  location.hash = r.canonical; // o hashchange aplica (assíncrono)
}

function onHashChange(): void {
  const prev = route.value;
  const h = location.hash;
  let r: ParseResult;
  let cause: RouteChangeCause;
  if (expected && expected.hash === h) {
    r = expected.result;
    cause = 'navigate';
  } else {
    r = parseRoute(h, ctx.value, prev);
    cause = 'history';
  }
  expected = null;
  if (r.changed && !r.holdHash && !ui.value.tv.on) replaceHash(r.canonical); // em TV, tv=1 fica (§5.2.7)
  closeIncoherentProfile(prev, r.route);
  commit(prev, r, cause);
}

/** Estáticos ou janela da linha do tempo chegaram (§5.2.9): relê o hash atual; grava com replace se o canônico mudou. */
function onContext(c: RouteContext): void {
  if (c === lastCtx) return;
  lastCtx = c;
  const prev = route.value;
  const r = parseRoute(location.hash, c, prev);
  if (r.changed && !r.holdHash && !ui.value.tv.on && !holdBare(location.hash)) replaceHash(r.canonical);
  if (sameRoute(prev, r.route)) {
    setRoutePending(r.pending);
    applyTitle(); // os nomes do título podem ter chegado agora
    return;
  }
  commit(prev, r, 'statics');
}

/**
 * Montagem (main.tsx, depois de gravar `config`, `layout` e `ctx`): lê o hash com `tv=1`, canoniza com `replaceState`
 * (sem entrada nova, AC-5.2-03) e instala os ouvintes. `tv=1` é ignorado com `features.tv = false` ou num modo de
 * celular (§5.2.7) e, aceito, fica na URL enquanto a TV estiver ligada. Chamar de novo reinstala. Devolve `stopRouter`.
 */
export function initRoute(hash: string = location.hash): () => void {
  stopRouter();
  const c = ctx.value;
  const r = parseRoute(hash, c, null);
  let next = r.route;
  if (next.tvOnEntry && PHONE_MODES.has(layout.value.mode)) next = { ...next, tvOnEntry: false };
  const target = next.tvOnEntry ? withTvParam(r.canonical) : r.canonical;
  if (target !== location.hash && !r.holdHash && !holdBare(location.hash)) replaceHash(target);
  setRoutePending(r.pending);
  route.value = next;
  applyTitle();

  lastCtx = c;
  expected = null;
  addEventListener('hashchange', onHashChange);
  const disposeCtx = effect(() => {
    const value = ctx.value;
    untracked(() => onContext(value));
  });
  const disposeSim = effect(() => {
    void isSimulated.value;
    untracked(applyTitle);
  });
  stopFn = () => {
    removeEventListener('hashchange', onHashChange);
    disposeCtx();
    disposeSim();
  };
  return stopRouter;
}

/** Desliga os ouvintes do roteador (testes e recriação). */
export function stopRouter(): void {
  const s = stopFn;
  stopFn = null;
  s?.();
  expected = null;
  lastCtx = null;
  setPendingThen(null);
}

/** Hash canônico da rota atual (§5.2.4; sem `tv`): o `ShareButton` monta o link com ele (§6.7). */
export function canonicalHash(): string {
  return formatRoute(route.value, ctx.value);
}

/** Saída da TV: grava a rota com `replaceState`, sem `tv=1` (§5.2.7). Chamada por `exitTv()`. */
export function dropTvHash(): void {
  replaceHash(canonicalHash());
}

/** Antes da recarga automática da TV (§12.8, DE-20): garante `tv=1` no hash com `replaceState`. */
export function ensureTvHash(): void {
  replaceHash(withTvParam(canonicalHash()));
}

/** Porta para `bindRouter(routerPort)` de state/actions.ts (FE §6.5). */
export const routerPort: RouterPortLike = Object.freeze({
  navigate,
  currentRoute,
  canonicalHash,
  dropTvHash,
  ensureTvHash,
});
